import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Search, X, Loader2, Film, Image as ImageIcon, FileText, Globe, Check } from "lucide-react";
import MediaStatusBadge from "./MediaStatusBadge";

/**
 * MediaPicker — Phase 2.
 *
 * Reusable modal that lets the admin/client pick a media URL by browsing
 * the project's (or client-scope's) Media Library instead of pasting a
 * raw URL. Falls back to upload + advanced "use external URL".
 *
 * Triggered by `MediaInput`'s new Browse button — when the user picks an
 * item, we hand back the public `/api/files/{path}` URL.
 *
 * Props:
 *  - open: boolean
 *  - onClose()
 *  - onPick(url, mediaItem)  — fires when the user confirms a selection
 *  - projectId?: scope library (and uploads) to a single project
 *  - accept?: file accept string for the inline upload input
 *  - kindHint?: 'image' | 'video' | 'panorama' | 'pdf' — pre-selects the type filter
 *  - preserveOriginal?: retain original image pixels when uploading a floor plan
 */
const KIND_OPTIONS = [
  { key: "all", label: "All", Icon: null },
  { key: "image", label: "Images", Icon: ImageIcon },
  { key: "video", label: "Videos", Icon: Film },
  { key: "panorama", label: "Panoramas", Icon: Globe },
  { key: "pdf", label: "PDFs", Icon: FileText },
];

export default function MediaPicker({ open, onClose, onPick, projectId, accept, kindHint, preserveOriginal = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [kind, setKind] = useState(kindHint || "all");
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [tab, setTab] = useState("library"); // library | url
  const [externalUrl, setExternalUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Reset whenever the picker opens
  useEffect(() => {
    if (open) {
      setTab("library");
      setSelectedId(null);
      setQ("");
      setKind(kindHint || "all");
      setExternalUrl("");
    }
  }, [open, kindHint]);

  // Load library when open / filters change
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (projectId) params.set("project_id", projectId);
    if (kind !== "all") params.set("type", kind);
    if (q.trim()) params.set("q", q.trim());
    api.get(`/admin/media?${params.toString()}`)
      .then((r) => { if (!cancelled) setItems(r.data?.items || []); })
      .catch((e) => { if (!cancelled) toast.error(apiErrorMessage(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, projectId, kind, q]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (projectId) fd.append("project_id", projectId);
      fd.append("kind", "upload");
      if (preserveOriginal) fd.append("preserve_original", "true");
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      // Re-fetch the library so the new item appears + select it.
      const params = new URLSearchParams();
      if (projectId) params.set("project_id", projectId);
      const next = await api.get(`/admin/media?${params.toString()}`);
      setItems(next.data?.items || []);
      setSelectedId(r.data.media_id);
    } catch (err) {
      if (err?.response?.status === 413) {
        toast.error(err.response.data?.detail || "Storage quota exceeded.");
      } else {
        toast.error(apiErrorMessage(err));
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const selected = useMemo(
    () => items.find((m) => m.id === selectedId),
    [items, selectedId]
  );

  const confirm = () => {
    if (tab === "url") {
      const u = externalUrl.trim();
      if (!u) { toast.error("Paste a URL first"); return; }
      onPick(u, null);
      onClose();
      return;
    }
    if (!selected) { toast.error("Pick a media item first"); return; }
    onPick(selected.url, selected);
    onClose();
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/55 backdrop-blur-sm flex items-center justify-center p-4 fade-in"
      onClick={onClose}
      data-testid="media-picker-modal"
    >
      <div
        className="w-full max-w-5xl h-[80vh] bg-white rounded-2xl shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-zinc-200">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-medium">Media Library</h3>
            <div className="flex gap-1 ml-3">
              <button
                onClick={() => setTab("library")}
                className={`text-xs px-3 py-1.5 rounded-md transition ${tab === "library" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}
                data-testid="media-picker-tab-library"
              >
                Library
              </button>
              <button
                onClick={() => setTab("url")}
                className={`text-xs px-3 py-1.5 rounded-md transition ${tab === "url" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}
                data-testid="media-picker-tab-url"
              >
                External URL
              </button>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900" data-testid="media-picker-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {tab === "library" && (
          <div className="flex-1 min-h-0 flex flex-col">
            {/* Toolbar */}
            <div className="flex items-center gap-3 px-5 py-3 border-b border-zinc-100 flex-wrap">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search filename or tags…"
                  className="w-72 pl-8 pr-3 py-1.5 rounded-md border border-zinc-300 text-sm outline-none focus:border-zinc-900"
                  data-testid="media-picker-search"
                />
              </div>
              <div className="flex gap-1" data-testid="media-picker-filters">
                {KIND_OPTIONS.map(({ key, label, Icon }) => (
                  <button
                    key={key}
                    onClick={() => setKind(key)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition inline-flex items-center gap-1.5 ${kind === key ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}
                    data-testid={`media-picker-filter-${key}`}
                  >
                    {Icon ? <Icon className="h-3 w-3" /> : null}
                    {label}
                  </button>
                ))}
              </div>
              <div className="ml-auto">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-zinc-900 text-white text-xs hover:bg-zinc-800 disabled:opacity-60 transition"
                  data-testid="media-picker-upload"
                >
                  {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  Upload new
                </button>
                <input ref={fileInputRef} type="file" accept={accept} className="hidden" onChange={handleUpload} />
              </div>
            </div>

            {/* Grid */}
            <div className="flex-1 min-h-0 overflow-auto scrollbar-thin p-5">
              {loading ? (
                <div className="text-sm text-zinc-500 text-center py-12">Loading…</div>
              ) : items.length === 0 ? (
                <div className="text-sm text-zinc-500 text-center py-12" data-testid="media-picker-empty">
                  No media in this scope yet. Upload to add the first item.
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="media-picker-grid">
                  {items.map((m) => (
                    <MediaCard
                      key={m.id}
                      media={m}
                      selected={selectedId === m.id}
                      onClick={() => setSelectedId(m.id)}
                      onDoubleClick={() => { setSelectedId(m.id); setTimeout(confirm, 0); }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "url" && (
          <div className="flex-1 flex flex-col items-center justify-center p-12 gap-3" data-testid="media-picker-url-tab">
            <div className="text-sm text-zinc-600 max-w-md text-center">
              Advanced: paste a direct media URL hosted elsewhere. Use this only when uploading isn't an option (e.g. partner-hosted CDN).
            </div>
            <input
              value={externalUrl}
              onChange={(e) => setExternalUrl(e.target.value)}
              placeholder="https://cdn.example.com/asset.mp4"
              className="w-full max-w-md px-3 py-2 rounded-md border border-zinc-300 text-sm outline-none focus:border-zinc-900"
              data-testid="media-picker-url-input"
            />
          </div>
        )}

        {/* Footer */}
        <div className="px-5 py-3 border-t border-zinc-200 flex items-center justify-between">
          <div className="text-xs text-zinc-500 truncate max-w-[60%]">
            {tab === "library" && selected ? (selected.title || selected.original_filename) : ""}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-1.5 rounded-md text-sm border border-zinc-300 hover:bg-zinc-50"
              data-testid="media-picker-cancel"
            >
              Cancel
            </button>
            <button
              onClick={confirm}
              disabled={tab === "library" ? !selectedId : !externalUrl.trim()}
              className="px-4 py-1.5 rounded-md text-sm bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="media-picker-confirm"
            >
              <Check className="h-4 w-4" /> Use this
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MediaCard({ media, selected, onClick, onDoubleClick }) {
  const lk = media.library_kind;
  const Icon = lk === "video" ? Film : lk === "pdf" ? FileText : lk === "panorama" ? Globe : ImageIcon;
  return (
    <button
      type="button"
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      className={`text-left rounded-lg overflow-hidden border-2 transition group ${selected ? "border-zinc-900 ring-2 ring-zinc-900/10" : "border-zinc-200 hover:border-zinc-300"}`}
      data-testid={`media-picker-card-${media.id}`}
      data-selected={selected || undefined}
    >
      <div className="aspect-video w-full bg-zinc-100 relative flex items-center justify-center">
        {media.poster_url ? (
          <img src={media.poster_url} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
        ) : lk === "image" || lk === "panorama" ? (
          <img src={media.thumbnail_url || media.url} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
        ) : (
          <Icon className="h-8 w-8 text-zinc-400" />
        )}
        {lk === "panorama" && (
          <span className="absolute top-1.5 right-1.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-black/55 text-white">360°</span>
        )}
        {lk === "video" && (
          <span className="absolute top-1.5 right-1.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-black/55 text-white">{media.duration_seconds ? `${media.duration_seconds.toFixed(1)}s` : "VIDEO"}</span>
        )}
      </div>
      <div className="px-2.5 py-1.5 bg-white">
        <div className="text-xs text-zinc-900 truncate font-medium" title={media.title || media.original_filename}>
          {media.title || media.original_filename || media.id}
        </div>
        <div className="text-[10px] text-zinc-500 flex gap-1.5 items-center mt-0.5">
          {media.video_width && <span>{media.video_width}×{media.video_height}</span>}
          {media.original_size && <span>· {formatKB(media.original_size)}</span>}
        </div>
        {media.url && media.url.includes("/api/files/") && (
          <div className="mt-1.5"><MediaStatusBadge url={media.url} mediaId={media.id} compact /></div>
        )}
      </div>
    </button>
  );
}

function formatKB(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
